'use client'

/**
 * Desk chip — Ant Tag. Side / status always include the word (LONG, SHORT, LIVE).
 * Never color-only.
 */

import React, { type ReactNode } from 'react'
import { Tag } from 'antd'
import type { TagProps } from 'antd'
import styles from './DeskChip.module.css'

export type DeskChipTone =
  | 'default'
  | 'success'
  | 'warning'
  | 'error'
  | 'primary'
  | 'buy'
  | 'sell'

export interface DeskChipProps extends Omit<TagProps, 'color'> {
  tone?: DeskChipTone
  children: ReactNode
}

export function DeskChip({
  tone = 'default',
  className = '',
  children,
  bordered = true,
  ...rest
}: DeskChipProps) {
  const toneClass = styles[tone] ?? styles.default
  return (
    <Tag
      bordered={bordered}
      className={`${styles.chip} ${toneClass} ${className}`.trim()}
      {...rest}
    >
      {children}
    </Tag>
  )
}

export type DeskSide = 'long' | 'short' | 'flat' | 'buy' | 'sell'

const SIDE_COPY: Record<DeskSide, string> = {
  long: 'LONG',
  short: 'SHORT',
  flat: 'FLAT',
  buy: 'BUY',
  sell: 'SELL',
}

export function DeskSideChip({
  side,
  className,
}: {
  side: DeskSide | string | null | undefined
  className?: string
}) {
  const key = String(side || '').toLowerCase() as DeskSide
  if (!key || !(key in SIDE_COPY)) {
    return (
      <DeskChip tone="default" className={className}>
        —
      </DeskChip>
    )
  }
  const tone: DeskChipTone =
    key === 'long' || key === 'buy' ? 'buy' : key === 'flat' ? 'default' : 'sell'
  return (
    <DeskChip tone={tone} className={className}>
      {SIDE_COPY[key]}
    </DeskChip>
  )
}
